/* eslint-disable react/no-array-index-key */
import useApi from '@propertypal/shared/src/hooks/useApi';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import dayjs from '@propertypal/shared/src/services/dayjs';
import { AuctionSearchResult } from '@propertypal/shared/src/types/auction';
import platform from '@propertypal/shared/src/utils/platform';
import AuctionBox from '@propertypal/web-ui/src/auction/AuctionBox';
import AuctionBoxPlaceholder from '@propertypal/web-ui/src/auction/AuctionBoxPlaceholder';
import AuctionsGrid from '@propertypal/web-ui/src/auction/AuctionsGrid.style';
import { Box } from '@propertypal/web-ui/src/layout';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import Tabs, { TabType } from '@propertypal/web-ui/src/tabs/Tabs';
import { Heading, Text } from '@propertypal/web-ui/src/typography';
import Head from 'next/head';
import { GetServerSideProps } from 'next/types';
import React, { FunctionComponent, useState } from 'react';
import generateMetaTags from '../components/layout/MetaTags';
import { requireAuthToken } from '../utils/auth';

interface Props {
  data: AuctionSearchResult;
  initialTab: string;
}

export const getServerSideProps: GetServerSideProps = requireAuthToken(async (ctx) => {
  const result = await axios(
    {
      url: `${API_URL}/auctions`,
    },
    undefined,
    ctx,
  );

  return {
    props: {
      data: result.data,
      initialTab: result.data.auctionDates.length
        ? result.data.auctionDates[0].split('T')[0]
        : dayjs().format('YYYY-MM-DD'),
    },
  };
});

const getTabs = (auctionDates?: string[] | null): TabType[] => {
  const today = dayjs();
  const tabs: TabType[] = [];

  for (let i = 0; i < 7; i += 1) {
    const weekday = today.add(i, 'day');
    const dateStr = weekday.format('YYYY-MM-DD');
    const disabled = !!(auctionDates && auctionDates.findIndex((d) => d.includes(dateStr)) < 0);

    tabs.push({
      key: dateStr,
      prefix: i === 0 ? 'Today' : weekday.format('ddd'),
      text: weekday.format('DD MMM'),
      disabled,
    });
  }

  return tabs;
};

const UpcomingAuctions: FunctionComponent<Props> = (props) => {
  const [data, , loading, fetchData] = useApi('/auctions', false, props.data);
  const [tab, setTab] = useState(props.initialTab);
  const tabs = getTabs(data?.auctionDates);

  const updateDate = async (date: string) => {
    setTab(date);
    fetchData(`/auctions?date=${date}`);
  };

  return (
    <>
      <Head>
        {generateMetaTags({
          title: 'Up-coming Property Auctions In Ireland And Northern Ireland',
          description:
            'PropertyPal Lists The Upcoming Property Auctions Across Ireland And Northern Ireland. Take A Look.',
          url: '/upcoming-auctions',
        })}
      </Head>

      <PageWrapper containerStyle={{ padding: '30px 0' }}>
        <Heading>Upcoming Auctions</Heading>
      </PageWrapper>

      {tabs && (
        <Tabs
          tabs={tabs}
          activeTab={tab}
          onTabClick={updateDate}
          containerStyle={{ backgroundColor: platform.select(undefined, 'white') }}
        />
      )}

      {loading && (
        <AuctionsGrid>
          {Array.from({ length: 3 }).map((_, i) => (
            <AuctionBoxPlaceholder key={i} />
          ))}
        </AuctionsGrid>
      )}

      {!loading && data?.results.length && (
        <AuctionsGrid data-testid="auction-results">
          {data.results.map((a, i) => (
            <AuctionBox key={i} auction={a} />
          ))}
        </AuctionsGrid>
      )}

      {!loading && !data?.results.length && (
        <Box ta="center" m="50px 0px" data-testid="empty-auctions" p="0px 12px">
          <Heading>No Auctions Found</Heading>

          <Text mt={5} data-testid="empty-text">
            Sorry. We couldn&apos;t find any auctions for {dayjs(tab, 'YYYY-MM-DD').format('dddd MMMM DD YYYY')}.
          </Text>
        </Box>
      )}
    </>
  );
};

export default UpcomingAuctions;
